// Pure helpers for the usage breakdown chart: key ranking and rolling
// per-day rows up into day/week buckets. The date range comes from the
// page-level filter (filters/dateRange).

import { DateRange } from '../filters/dateRange';

export type TokenKind = 'input' | 'converted' | 'supported' | 'output';

interface UsageRowBase {
  date: string; // YYYY-MM-DD
  keyId: number;
  keyLabel: string;
  // Row count behind the tokens: operations for CLI, requests for the API.
  count?: number;
}

export interface UsageRow extends UsageRowBase {
  tokens: number;
}

// CLI rows. The chart picks one kind as the tokens on screen.
export interface KindUsageRow extends UsageRowBase {
  tokensByKind: Record<TokenKind, number>;
}

export type Granularity = 'day' | 'week';
export type Metric = 'tokens' | 'count';

export function metricValue(row: UsageRow, metric: Metric): number {
  return metric === 'count' ? row.count ?? 0 : row.tokens;
}

// The server sends the union of its top keys per metric.
// Draw this many for whichever metric is on screen.
export const MAX_VISIBLE_KEYS = 7;
export const OTHER_KEY = 'other';
// The server folds everything beyond the top keys into this synthetic id.
export const OTHER_KEY_ID = 0;

// Validated dark categorical slots (dataviz reference palette, dark mode).
export const SERIES_COLORS = [
  '#3987e5',
  '#199e70',
  '#c98500',
  '#008300',
  '#9085e9',
  '#e66767',
  '#d55181',
  '#d95926',
];
export const OTHER_COLOR = '#898781';

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseISODate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function filterRows(rows: UsageRow[], range: DateRange): UsageRow[] {
  return rows.filter((r) => r.date >= range.from && r.date <= range.to);
}

export interface KeyInfo {
  keyId: number;
  keyLabel: string;
  tokens: number;
  count: number;
}

// Rank descending. Token ties use count, then id. Count ties use id.
export function rankKeys(rows: UsageRow[], metric: Metric = 'tokens'): KeyInfo[] {
  const byId = new Map<number, KeyInfo>();
  for (const r of rows) {
    if (r.keyId === OTHER_KEY_ID) continue; // never a real series
    const info = byId.get(r.keyId);
    if (info) {
      info.tokens += r.tokens;
      info.count += r.count ?? 0;
    } else {
      byId.set(r.keyId, { keyId: r.keyId, keyLabel: r.keyLabel, tokens: r.tokens, count: r.count ?? 0 });
    }
  }
  const primary = (k: KeyInfo) => (metric === 'count' ? k.count : k.tokens);
  const secondary = (k: KeyInfo) => (metric === 'count' ? 0 : k.count);
  return Array.from(byId.values()).sort(
    (a, b) => primary(b) - primary(a) || secondary(b) - secondary(a) || a.keyId - b.keyId
  );
}

// Monday of the week containing d.
function weekStart(d: Date): Date {
  const copy = new Date(d);
  const day = (copy.getDay() + 6) % 7; // 0 = Monday
  copy.setDate(copy.getDate() - day);
  return copy;
}

function bucketLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export interface Bucket {
  bucket: string; // YYYY-MM-DD of bucket start
  label: string;
  total: number;
  // per-key values under "k<keyId>", folded tail under OTHER_KEY
  [series: string]: string | number;
}

// Continuous buckets over the range (gaps filled with zeros). Values for keys
// in visibleKeyIds go under "k<keyId>"; everything else sums into OTHER_KEY.
export function bucketize(
  rows: UsageRow[],
  range: DateRange,
  granularity: Granularity,
  visibleKeyIds: number[],
  metric: Metric = 'tokens'
): Bucket[] {
  const visible = new Set(visibleKeyIds);
  const buckets = new Map<string, Bucket>();

  const makeBucket = (start: Date): Bucket => {
    const b: Bucket = { bucket: toISODate(start), label: bucketLabel(start), total: 0 };
    for (const id of visibleKeyIds) b[`k${id}`] = 0;
    b[OTHER_KEY] = 0;
    return b;
  };

  let cursor = granularity === 'week' ? weekStart(parseISODate(range.from)) : parseISODate(range.from);
  const end = parseISODate(range.to);
  while (cursor <= end) {
    buckets.set(toISODate(cursor), makeBucket(cursor));
    cursor = addDays(cursor, granularity === 'week' ? 7 : 1);
  }

  for (const r of filterRows(rows, range)) {
    const day = parseISODate(r.date);
    const start = granularity === 'week' ? weekStart(day) : day;
    const b = buckets.get(toISODate(start));
    if (!b) continue;
    const value = metricValue(r, metric);
    const series = visible.has(r.keyId) ? `k${r.keyId}` : OTHER_KEY;
    b[series] = (b[series] as number) + value;
    b.total += value;
  }

  return Array.from(buckets.values());
}

interface ConvertedTokensInput {
  date: string;
  input_tokens_converted_metered: number;
  input_tokens_converted_duplicate: number;
}

export interface ConvertedTokensDay {
  label: string;
  metered: number;
  duplicate: number;
}

export function convertedTokensByDay(days: ConvertedTokensInput[], range: DateRange): ConvertedTokensDay[] {
  const rows = new Map<string, ConvertedTokensDay>();
  const end = parseISODate(range.to);
  for (let day = parseISODate(range.from); day <= end; day = addDays(day, 1)) {
    rows.set(toISODate(day), { label: bucketLabel(day), metered: 0, duplicate: 0 });
  }
  for (const day of days) {
    const row = rows.get(day.date);
    if (!row) continue;
    row.metered += day.input_tokens_converted_metered;
    row.duplicate += day.input_tokens_converted_duplicate;
  }
  return Array.from(rows.values());
}
